import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import React from 'react';

import { PortalPanel, SubmissionCompleteView } from './PortalPanel';

interface PortalTabProps {
  caseId: string;
  caseDetails: GetCaseRecordResponse | undefined;
  isTerminalState: boolean;
  onStartSession?: () => void;
}

/**
 * Portal tab content. In terminal states, shows the submission confirmation —
 * the captured screenshot if one exists, otherwise a "no submission" message.
 *
 * The screenshot is shown independent of whether the portal returned an ack id.
 * Ack-less portals (e.g. FDA MedWatch, which issues no on-screen reference and
 * acknowledges by email) still capture a confirmation screenshot, so gating
 * this on `ack_id` wrongly hid the screenshot and claimed "no portal submission
 * was made". The screenshot asset — not the ack id — is the proof of
 * submission, so `SubmissionCompleteView` decides based on the asset.
 *
 * Otherwise, mounts PortalPanel which owns the session lifecycle.
 */
export const PortalTab: React.FC<PortalTabProps> = ({
  caseId,
  caseDetails,
  isTerminalState,
  onStartSession,
}) => {
  if (isTerminalState) {
    return <SubmissionCompleteView caseId={caseId} showEmptyWhenMissing />;
  }

  return <PortalPanel caseId={caseId} caseDetails={caseDetails} onStartSession={onStartSession} />;
};
